import { useEffect, useMemo } from "react";
import type { SxProps, Theme } from "@mui/material";
import { gridClasses } from "@mui/x-data-grid";
import BaseTable from "@components/DataTable/BaseTable";
import type { Header } from "@components/DataTable/CustomDataTable.types";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import { getPortServiceStatusInfo } from "@features/vessel-call/vessel-call-status/PortServices/preview/utils";
import type { PortService, PortServicesStatusLog } from "@services/port-services/types";
import { getPortServicesStatusLogs } from "@services/portServicesApi";
import { useQuery } from "@tanstack/react-query";

const STATUS_LOGS_COLUMNS = Object.freeze({
  status: "Status",
  changedBy: "Changed By",
  changedDate: "Changed Date",
});

const customSx: SxProps<Theme> = (theme) => ({
  "border": "none",
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: `${theme.palette.background.menu} !important`,
    borderBottom: "none !important",
  },
  [`& .${gridClasses.cell}`]: {
    border: 0,
  },

  // Alternate row colors
  [`& .${gridClasses.row}:nth-of-type(even)`]: {
    backgroundColor: `${theme.palette.background.navigation}`,
  },
  [`& .${gridClasses.row}.odd`]: {
    backgroundColor: `${theme.palette.background.default}`,
  },
});

const columnsToExcludeFromGrid: string[] = ["id"];
const dateTimeFields = ["changedDate"];
interface StatusLogProps {
  portServiceDetails: PortService;
  shouldRefetch: boolean;
  setShouldRefetch: (arg: boolean) => void;
}
const StatusLog = ({ portServiceDetails, shouldRefetch, setShouldRefetch }: StatusLogProps) => {
  const {
    data: statusLogs = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["getPortServicesStatusLogs"],
    queryFn: () => {
      return getPortServicesStatusLogs("bff-vessel-call", portServiceDetails.id);
    },
    // Options to re-fetch on every mount:
    staleTime: 0,
    refetchOnMount: true,
  });

  const formattedRows = statusLogs
    .filter((row) => row.status)
    .map((row: PortServicesStatusLog) => {
      const statusInfo = getPortServiceStatusInfo(row.status, portServiceDetails.serviceCategory);
      return {
        ...row,
        status: statusInfo.label,
        badgeType: statusInfo.badgeType,
      };
    });

  const headers: Header[] = useMemo(
    () =>
      Object.keys(STATUS_LOGS_COLUMNS)
        .filter((key) => !columnsToExcludeFromGrid.includes(key))
        .map((key) => ({
          field: key.toString(),
          columnName: STATUS_LOGS_COLUMNS[key as keyof typeof STATUS_LOGS_COLUMNS].toString(),
          minWidth: 52,
          type: dateTimeFields.includes(String(key))
            ? "dateTime"
            : key === "status"
              ? "logsStatus"
              : undefined,
        })),
    [],
  );

  useEffect(() => {
    if (shouldRefetch) {
      refetch();
      setShouldRefetch(false);
    }
  }, [shouldRefetch, refetch]);

  if (isLoading) {
    return <LoadingOverlay loading={isLoading} />;
  }

  return (
    <div className="port-service-preview-status-log-table">
      <BaseTable
        rows={formattedRows}
        headers={headers}
        customSx={customSx}
        onRowClick={(_, e) => {
          e.stopPropagation();
        }}
      />
    </div>
  );
};

export default StatusLog;
